import {
  catalogPluginRoleTabs,
  catalogPluginSourceTabs,
  catalogStatuses,
  filterPluginCatalogRows,
  getPluginCatalogRow,
  normalizeCatalogPluginRoleFilter,
  normalizeCatalogPluginSourceFilter,
  pluginCatalogSourceBadgeLabel,
  pluginCatalogSourceDetailLabel,
  pluginCatalogTrustBadgeMeta,
  pluginCatalogRows,
  type CatalogPluginRoleFilter,
  type CatalogPluginRow,
  type CatalogPluginSourceFilter,
} from '@okrapdf/ecosystem-catalog';
import { OkraRuntimeError } from '../../errors';
import { Data, Effect } from 'effect';

export class PluginCatalogCommandError extends Data.TaggedError('PluginCatalogCommandError')<{
  readonly method: 'runPluginCatalog';
  readonly status?: number;
}> {}

const pluginCatalogCommandErrorCauses = new WeakMap<PluginCatalogCommandError, unknown>();

function pluginCatalogCommandFailure(cause: unknown): PluginCatalogCommandError {
  const error = new PluginCatalogCommandError({
    method: 'runPluginCatalog',
    ...(cause instanceof OkraRuntimeError ? { status: cause.status } : {}),
  });
  pluginCatalogCommandErrorCauses.set(error, cause);
  return error;
}

export function pluginCatalogCommandErrorCause(error: PluginCatalogCommandError): unknown {
  return pluginCatalogCommandErrorCauses.get(error) ?? error;
}

export interface PluginCatalogListOptions {
  role?: string;
  status?: string;
  source?: string;
}

export type PluginCatalogRow = CatalogPluginRow;

export interface PluginCatalogRunInvokeResult {
  run_id: string;
  status: 'ok' | 'error';
  duration_ms: number;
  output?: unknown;
  error?: string;
  logs?: readonly string[];
}

export interface PluginCatalogRunResult extends PluginCatalogRunInvokeResult {
  object: 'okra.plugin.run';
  plugin_id: string;
  source_kind: 'legacy_surface';
  invoked_as: 'legacy_facet';
  legacy_id: string;
}

export type PluginCatalogRunInvoker = (
  legacyId: string,
  payload: unknown,
) => Promise<PluginCatalogRunInvokeResult>;

interface NormalizedPluginCatalogListOptions {
  role?: CatalogPluginRoleFilter;
  status?: string;
  source?: CatalogPluginSourceFilter;
}

const catalogPluginStatusFilters = ['all', ...catalogStatuses] as const;

function normalizeFilterValue(value: string | undefined): string | undefined {
  return value?.trim().toLowerCase() || undefined;
}

function normalizeStatusFilter(value: string | undefined): string | undefined {
  const normalized = normalizeFilterValue(value);
  if (!normalized) return undefined;
  return catalogPluginStatusFilters.includes(
    normalized as (typeof catalogPluginStatusFilters)[number],
  )
    ? normalized
    : undefined;
}

function invalidPluginCatalogFilter(
  field: 'role' | 'status' | 'source',
  value: string,
  allowedValues: readonly string[],
): OkraRuntimeError {
  const message = `Invalid plugin catalog ${field} filter "${value}". Use one of: ${allowedValues.join(', ')}.`;
  return new OkraRuntimeError('INVALID_REQUEST', message, 400, {
    error: 'invalid_plugin_catalog_filter',
    field,
    value,
    allowed_values: allowedValues,
    message,
    next_actions: [
      {
        cmd: `okra plugin list --${field} ${allowedValues.find((allowed) => allowed !== 'all') ?? allowedValues[0]}`,
        why: `Filter with a supported ${field} value.`,
      },
    ],
  });
}

function normalizeListOptions(
  options: PluginCatalogListOptions,
): NormalizedPluginCatalogListOptions {
  const role = normalizeCatalogPluginRoleFilter(options.role);
  const status = normalizeStatusFilter(options.status);
  const source = normalizeCatalogPluginSourceFilter(options.source);

  if (normalizeFilterValue(options.role) && !role) {
    throw invalidPluginCatalogFilter(
      'role',
      options.role!,
      catalogPluginRoleTabs.map((tab) => tab.id),
    );
  }
  if (normalizeFilterValue(options.status) && !status) {
    throw invalidPluginCatalogFilter('status', options.status!, catalogPluginStatusFilters);
  }
  if (normalizeFilterValue(options.source) && !source) {
    throw invalidPluginCatalogFilter(
      'source',
      options.source!,
      catalogPluginSourceTabs.map((tab) => tab.id),
    );
  }

  return {
    role,
    status,
    source,
  };
}

export function listPluginCatalog(options: PluginCatalogListOptions = {}): PluginCatalogRow[] {
  return [...filterPluginCatalogRows(pluginCatalogRows, normalizeListOptions(options))];
}

export function getPluginCatalogInfo(id: string): PluginCatalogRow {
  const row = getPluginCatalogRow(id);
  if (!row) {
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      `Plugin "${id}" was not found in the catalog`,
      404,
      {
        error: 'plugin_not_found',
        message: `No plugin catalog row exists with id "${id}".`,
        next_actions: [
          { cmd: 'okra plugin list', why: 'List known plugin ids.' },
          {
            cmd: `okra plugin add parser ${id}`,
            why: 'Scaffold a new parser descriptor if this is a new plugin.',
          },
        ],
      },
    );
  }
  return row;
}

export const runPluginCatalogEffect = Effect.fn('okrapdf/cli.runPluginCatalog')(
  (
    id: string,
    payload: unknown,
    invokeLegacyFacet: PluginCatalogRunInvoker,
  ): Effect.Effect<PluginCatalogRunResult, PluginCatalogCommandError> =>
    Effect.tryPromise({
      try: () => runPluginCatalogPromise(id, payload, invokeLegacyFacet),
      catch: pluginCatalogCommandFailure,
    }),
);

export async function runPluginCatalog(
  id: string,
  payload: unknown,
  invokeLegacyFacet: PluginCatalogRunInvoker,
): Promise<PluginCatalogRunResult> {
  return Effect.runPromise(
    runPluginCatalogEffect(id, payload, invokeLegacyFacet).pipe(
      Effect.catch((error) => Effect.fail(pluginCatalogCommandErrorCause(error))),
    ),
  );
}

async function runPluginCatalogPromise(
  id: string,
  payload: unknown,
  invokeLegacyFacet: PluginCatalogRunInvoker,
): Promise<PluginCatalogRunResult> {
  const row = getPluginCatalogInfo(id);
  if (row.source_kind !== 'legacy_surface') {
    const nextActions = [
      {
        cmd: `okra plugin info ${row.id}`,
        why: 'Inspect this descriptor-backed plugin row and its trust/package metadata.',
      },
      {
        cmd: 'okra plugin list --source legacy_surface',
        why: 'List plugin rows currently invokable through the legacy facet bridge.',
      },
    ];
    if (row.npm_package) {
      nextActions.push({
        cmd: `pnpm --filter ${row.npm_package} typecheck`,
        why: 'Validate the descriptor-owned package stub while runtime execution is wired separately.',
      });
    }
    throw new OkraRuntimeError(
      'INVALID_REQUEST',
      `Plugin "${row.id}" is not runnable through the CLI bridge yet`,
      400,
      {
        error: 'plugin_run_not_available',
        plugin_id: row.id,
        source_kind: row.source_kind,
        descriptor_id: row.descriptor_id,
        runtime: row.runtime,
        npm_package: row.npm_package,
        package_path: row.package_path,
        message:
          'okra plugin run currently bridges legacy facet-backed plugin rows. Descriptor-backed plugin execution belongs to the runtime adapter layer.',
        next_actions: nextActions,
      },
    );
  }

  const legacyId = row.legacy_ids[0] ?? row.id;
  const result = await invokeLegacyFacet(legacyId, payload ?? {});
  return {
    ...result,
    object: 'okra.plugin.run',
    plugin_id: row.id,
    source_kind: 'legacy_surface',
    invoked_as: 'legacy_facet',
    legacy_id: legacyId,
  };
}

export function formatPluginCatalogList(rows: readonly PluginCatalogRow[]): string {
  if (rows.length === 0) return 'No plugins matched.';
  const lines = [
    'Plugins',
    ...rows.map((row) => {
      const hosts = row.allowed_hosts.length ? row.allowed_hosts.join(',') : 'zero-network';
      const source = pluginCatalogSourceBadgeLabel(row.source_kind);
      const trust = pluginCatalogTrustBadgeMeta(row).label;
      return `  ${row.id}\t${row.role}\t${row.status}\t${source}\t${trust}\t${hosts}\t${row.name}`;
    }),
  ];
  return lines.join('\n');
}

export function formatPluginCatalogInfo(row: PluginCatalogRow): string {
  const trustMeta = pluginCatalogTrustBadgeMeta(row);
  const lines = [
    `${row.name} (${row.id})`,
    row.description,
    '',
    `Role: ${row.role}`,
    `Status: ${row.status}`,
    `Trust: ${trustMeta.label}`,
    `Runtime: ${row.runtime ?? 'unknown'}`,
    `Format: ${row.format ?? 'unknown'}`,
    `Allowed hosts: ${row.allowed_hosts.length ? row.allowed_hosts.join(', ') : 'zero-network'}`,
    `Source: ${pluginCatalogSourceDetailLabel(row)}`,
    `Catalog page: ${row.href}`,
  ];
  if (row.legacy_ids.length) lines.push(`Legacy ids: ${row.legacy_ids.join(', ')}`);
  if (row.package_path) lines.push(`Package path: ${row.package_path}`);
  return lines.join('\n');
}

export function formatPluginCatalogRunResult(result: PluginCatalogRunResult): string {
  const lines = [
    `${result.run_id}  ${result.status}  ${result.duration_ms}ms`,
    `plugin: ${result.plugin_id}`,
    `bridge: ${result.invoked_as}`,
    `legacy id: ${result.legacy_id}`,
    '',
    'Output:',
    JSON.stringify(result.output ?? null, null, 2),
  ];
  if (result.error) {
    lines.push('', `Error: ${result.error}`);
  }
  return lines.join('\n');
}
